import { useEffect, useMemo, useState } from 'react';

import Icon from './Icon.jsx';
import PracticeRunner from './PracticeRunner.jsx';
import {
  copyToClipboard,
  dailyNumber,
  dailyQuestions,
  millisUntilNext,
  shareText,
} from '../lib/daily.js';

/**
 * The Daily.
 *
 * Five questions, the same five for everyone, once a day. Two states: play it,
 * or — having played — see what you got and a grid you can paste anywhere.
 *
 * You cannot replay it. That is the feature, not a limitation: a score is only
 * worth posting if everyone had one attempt at the same questions, and an app
 * that lets you retry until you get five out of five has nothing to post about.
 */

function Countdown({ until }) {
  const [left, setLeft] = useState(until);

  useEffect(() => {
    setLeft(until);
    const timer = setInterval(() => setLeft((ms) => Math.max(0, ms - 1000)), 1000);
    return () => clearInterval(timer);
  }, [until]);

  const hours = Math.floor(left / 3_600_000);
  const minutes = Math.floor((left % 3_600_000) / 60_000);
  const seconds = Math.floor((left % 60_000) / 1000);
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <span className="daily__clock" aria-label="Time until the next Daily">
      {pad(hours)}:{pad(minutes)}:{pad(seconds)}
    </span>
  );
}

export default function DailyChallenge({ daily, soundOn, onAnswer, onComplete, onExit, onToggleSound }) {
  const day = useMemo(() => dailyNumber(), []);
  const questions = useMemo(() => dailyQuestions(day), [day]);
  const [copied, setCopied] = useState(false);

  const alreadyPlayed = daily.day === day;
  const results = alreadyPlayed ? daily.results : [];

  // ── Not played yet: it is just the quiz, with no way back mid-run ─────────
  if (!alreadyPlayed) {
    return (
      <PracticeRunner
        key={`daily-${day}`}
        questions={questions}
        isPremium
        soundOn={soundOn}
        title={`The Daily · #${day}`}
        blurb="Five questions. Everyone gets the same five today."
        onToggleSound={onToggleSound}
        onAnswer={onAnswer}
        onFinish={(correct, total, marks) => onComplete(day, correct, total, marks)}
        onExit={onExit}
      />
    );
  }

  // ── Played: the result, and something to paste ────────────────────────────
  const score = results.filter(Boolean).length;
  const text = shareText(day, results, daily.streak);

  const verdict =
    score === results.length
      ? 'Perfect.'
      : score >= results.length - 1
        ? 'Close to it.'
        : score >= Math.ceil(results.length / 2)
          ? 'Solid.'
          : 'Tomorrow is another one.';

  return (
    <section className="screen daily">
      <header className="screen__heading">
        <h2>The Daily · #{day}</h2>
        <p>Done for today. The next five arrive at midnight, UTC.</p>
      </header>

      <div className="daily__result">
        <div className="daily__grid" aria-label={`${score} out of ${results.length}`}>
          {results.map((right, i) => (
            <span key={i} className={`daily__square${right ? ' is-right' : ''}`} />
          ))}
        </div>

        <p className="daily__score">
          <strong>
            {score}/{results.length}
          </strong>
          <span>{verdict}</span>
        </p>

        <div className="daily__stats">
          <div>
            <span className="daily__stat-value">{daily.streak}</span>
            <span className="daily__stat-label">day{daily.streak === 1 ? '' : 's'} running</span>
          </div>
          <div>
            <span className="daily__stat-value">{daily.best}</span>
            <span className="daily__stat-label">best run</span>
          </div>
          <div>
            <span className="daily__stat-value">{daily.played}</span>
            <span className="daily__stat-label">played</span>
          </div>
        </div>

        <button
          type="button"
          className="btn btn--primary btn--block btn--lg"
          onClick={async () => {
            // Prefer the OS share sheet on a phone — it is one tap to a group
            // chat, where this is actually meant to end up.
            if (navigator.share) {
              try {
                await navigator.share({ text });
                return;
              } catch {
                /* dismissed, or unsupported for text — fall through to copy */
              }
            }
            if (await copyToClipboard(text)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 2200);
            }
          }}
        >
          <Icon name="share" size={16} />
          {copied ? 'Copied' : 'Share your result'}
        </button>

        {/* Shown so people can see exactly what they are about to post — no
            question text, no answers, nothing that spoils it for the reader. */}
        <pre className="daily__preview">{text}</pre>

        <p className="daily__next">
          Next Daily in <Countdown until={millisUntilNext()} />
        </p>
      </div>

      <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
        Back to practice
      </button>
    </section>
  );
}
